import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { Loader2, ImageOff, Plus, Trash2, FileText, Upload, X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { api, uploadDocument, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]";
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const emptyAttr = () => ({ _uid: uid(), label: "", value: "" });
const BASIS_LABEL = { per_bottle: "per bottle", per_case: "per case", per_pallet: "per pallet", per_litre_parent: "per volume of water" };
const FILM_MATERIALS = [
  { key: "LLDPE", label: "LLDPE", density: 0.928 },
  { key: "LDPE", label: "LDPE", density: 0.925 },
  { key: "HDPE", label: "HDPE", density: 0.953 },
  { key: "PP", label: "PP", density: 0.908 },
  { key: "PET", label: "PET", density: 1.365 },
  { key: "PS", label: "PS", density: 1.055 },
  { key: "PA", label: "PA (Nylon)", density: 1.135 },
  { key: "EVA", label: "EVA", density: 0.945 },
  { key: "PVC", label: "PVC", density: 1.3 },
];

const dUid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const migrateAssocs = (d, components) => {
  if (d.associations?.length) return d.associations.map((a) => ({ _uid: dUid(), ...a }));
  return [d.associated_cap, d.associated_water_type, d.associated_additive].filter(Boolean).map((vid) => {
    const c = (components || []).find((cc) => (cc.types || []).some((t) => t.id === vid));
    return { _uid: dUid(), id: dUid(), component_id: c?.id || "", variant_id: vid, dose_g_per_l: null };
  });
};

export default function TypeDialog({ open, onOpenChange, onSubmit, initial, title, variationLabel, itemLabel, isLabel, components = [], component = null }) {
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetUploading, setSheetUploading] = useState(false);
  const sheetRef = useRef(null);
  const lenSource = useRef(null);   // null | "manual" | "derived"
  const netSource = useRef(null);   // null | "manual" | "specs" | "gross"
  const [unitsPerCase, setUnitsPerCase] = useState("");
  const [casesPerPallet, setCasesPerPallet] = useState("");
  const [unitsPerPallet, setUnitsPerPallet] = useState("");
  const [displayUnit, setDisplayUnit] = useState("");
  const [stockUnitName, setStockUnitName] = useState("");
  const [contentPerStockUnit, setContentPerStockUnit] = useState("");
  const [weightPerUnit, setWeightPerUnit] = useState("");
  const [weightUnit, setWeightUnit] = useState("g");
  const [rollsPerCase, setRollsPerCase] = useState("");
  const [packagingMode, setPackagingMode] = useState("standard");
  const [rollContentType, setRollContentType] = useState("units");
  const [rollGrossWeight, setRollGrossWeight] = useState("");
  const [rollNetWeight, setRollNetWeight] = useState("");
  const [rollCoreWeight, setRollCoreWeight] = useState("");
  const [rollWeightUnit, setRollWeightUnit] = useState("lbs");
  const [filmWidth, setFilmWidth] = useState("");
  const [filmWidthUnit, setFilmWidthUnit] = useState("in");
  const [filmThickness, setFilmThickness] = useState("");
  const [filmThicknessUnit, setFilmThicknessUnit] = useState("um");
  const [materialDensity, setMaterialDensity] = useState("");
  const [filmMaterial, setFilmMaterial] = useState("");
  const [packageType, setPackageType] = useState("");
  const [packagesPerPallet, setPackagesPerPallet] = useState("");
  const [palletWeight, setPalletWeight] = useState("");
  const [palletWeightUnit, setPalletWeightUnit] = useState("lbs");
  const [palletDims, setPalletDims] = useState("");
  const [recipe, setRecipe] = useState([]);
  const forceRolls = ["film", "label"].includes(component?.inventory_mode_effective || component?.inventory_mode || "");
  const isFilm = (component?.inventory_mode_effective || component?.inventory_mode || "") === "film";
  const isAlum = (component?.materials || (component?.material ? [component.material] : [])).includes("ALUM");
  const [coPackers, setCoPackers] = useState([]);
  useEffect(() => {
    if (open && isLabel) api.get("/co-packers").then((r) => setCoPackers(r.data || [])).catch(() => {});
  }, [open, isLabel]);
  const sourceOptions = [
    { id: "waterco", name: "WaterCo Springs" },
    ...coPackers.filter((cp) => (cp.status || "active") === "active").map((cp) => ({ id: cp.id, name: cp.company_name })),
  ];

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setImageUrl(initial?.image_url || "");
      setDescription(initial?.description || "");
      setAttributes(initial?.attributes?.length ? initial.attributes.map((a) => ({ _uid: uid(), ...a })) : [emptyAttr()]);
      setDesigns(initial?.designs ? initial.designs.map((d) => ({ ...d, associations: migrateAssocs(d, components) })) : []);
      setSheetUrl(initial?.tech_data_sheet_url || "");
      setSheetName(initial?.tech_data_sheet_name || "");
      setUnitsPerCase(initial?.units_per_case != null ? String(initial.units_per_case) : "");
      setCasesPerPallet(initial?.cases_per_pallet != null ? String(initial.cases_per_pallet) : "");
      setUnitsPerPallet(initial?.units_per_pallet != null ? String(initial.units_per_pallet) : "");
      setDisplayUnit(initial?.display_unit || "");
      setStockUnitName(initial?.stock_unit_name || "");
      setContentPerStockUnit(initial?.content_per_stock_unit != null ? String(initial.content_per_stock_unit) : "");
      setWeightPerUnit(initial?.weight_per_unit != null ? String(initial.weight_per_unit) : "");
      setWeightUnit(initial?.weight_unit || "g");
      setRollsPerCase(initial?.rolls_per_case != null ? String(initial.rolls_per_case) : "");
      setPackagingMode(forceRolls ? "rolls" : (initial?.packaging_mode || (initial?.rolls_per_case != null ? "rolls" : "standard")));
      const _d = component?.dimension || "count";
      const _rct = initial?.roll_content_type;
      setRollContentType(isFilm ? (["ft", "m"].includes(_rct) ? _rct : "m") : (["units", "ft", "m"].includes(_rct) ? _rct : (_d === "length" ? "m" : "units")));
      setRollGrossWeight(initial?.roll_gross_weight != null ? String(initial.roll_gross_weight) : "");
      setRollCoreWeight(initial?.roll_core_weight != null ? String(initial.roll_core_weight) : "");
      setRollWeightUnit(initial?.roll_weight_unit || "lbs");
      setRollNetWeight(initial?.roll_net_weight != null ? String(initial.roll_net_weight)
        : (initial?.roll_gross_weight != null ? String(Math.max(0, initial.roll_gross_weight - (initial.roll_core_weight || 0))) : ""));
      lenSource.current = initial?.content_per_stock_unit != null ? "manual" : null;
      const _specsDerivable = ["ft", "m"].includes(_rct || "") && (initial?.film_width || 0) > 0 && (initial?.film_thickness || 0) > 0 && (initial?.material_density || 0) > 0 && (initial?.content_per_stock_unit || 0) > 0;
      netSource.current = _specsDerivable ? "specs" : (initial?.roll_net_weight != null ? "manual" : (initial?.roll_gross_weight != null ? "gross" : null));
      setFilmWidth(initial?.film_width != null ? String(initial.film_width) : "");
      setFilmWidthUnit(initial?.film_width_unit || "in");
      setFilmThickness(initial?.film_thickness != null ? String(initial.film_thickness) : "");
      setFilmThicknessUnit(initial?.film_thickness_unit || "um");
      setMaterialDensity(initial?.material_density != null ? String(initial.material_density) : "");
      setFilmMaterial(initial?.film_material || FILM_MATERIALS.find((m) => m.density === initial?.material_density)?.key || "");
      setPackageType(initial?.package_type || "");
      setPackagesPerPallet(initial?.packages_per_pallet != null ? String(initial.packages_per_pallet) : "");
      setPalletWeight(initial?.weight_per_pallet != null ? String(initial.weight_per_pallet) : "");
      setPalletWeightUnit(initial?.pallet_weight_unit || "lbs");
      setPalletDims(initial?.pallet_dims || "");
      setRecipe(initial?.recipe ? initial.recipe.map((r) => ({ ...r })) : []);
      setImgError(false);
    }
  }, [open, initial, components, component?.dimension, forceRolls, isFilm]);

  const handleSheet = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSheetUploading(true);
    try {
      const { url } = await uploadDocument(f);
      setSheetUrl(url);
      setSheetName(f.name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setSheetUploading(false);
    }
  };

  const updateAttr = (i, field, val) => setAttributes((p) => p.map((a, idx) => (idx === i ? { ...a, [field]: val } : a)));
  const addAttr = () => setAttributes((p) => [...p, emptyAttr()]);
  const removeAttr = (i) => setAttributes((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : [emptyAttr()]));

  const addDesign = () => setDesigns((p) => [...p, { id: dUid(), name: "", associated_cap: "", associated_water_type: "", associated_additive: "", artwork_image_url: "", revision: "", notes: "", associations: [] }]);
  const patchDesign = (i, patch) => setDesigns((p) => p.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDesign = (i) => setDesigns((p) => p.filter((_, idx) => idx !== i));
  const addAssoc = (i) => setDesigns((p) => p.map((d, idx) => (idx === i ? { ...d, associations: [...(d.associations || []), { _uid: dUid(), id: dUid(), component_id: "", variant_id: "", dose_g_per_l: null }] } : d)));
  const patchAssoc = (i, ai, patch) => setDesigns((p) => p.map((d, idx) => (idx === i ? { ...d, associations: (d.associations || []).map((a, aidx) => (aidx === ai ? { ...a, ...patch } : a)) } : d)));
  const removeAssoc = (i, ai) => setDesigns((p) => p.map((d, idx) => (idx === i ? { ...d, associations: (d.associations || []).filter((_, aidx) => aidx !== ai) } : d)));

  const handleBarcode = async (i, prefix, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const { url } = await uploadDocument(f);
      patchDesign(i, { [`${prefix}_upc_barcode_url`]: url, [`${prefix}_upc_barcode_name`]: f.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Barcode upload failed");
    }
  };

  const dim = component?.dimension || "count";
  const weightLabel = dim === "volume" ? "Weight per mL (density)" : dim === "length" ? "Weight per metre" : dim === "area" ? "Weight per m²" : "Weight per unit (each)";
  const rUid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
  const addRecipe = () => setRecipe((p) => [...p, { id: rUid(), component_id: "", variant_id: "", mode: "fixed", quantity: "", unit: "", basis: dim === "volume" ? "per_litre_parent" : "per_bottle" }]);
  const patchRecipe = (i, patch) => setRecipe((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRecipe = (i) => setRecipe((p) => p.filter((_, idx) => idx !== i));
  const variantsOf = (cid) => ((components || []).find((x) => x.id === cid)?.types || []);

  // Anchor-based cross-field sync; runs only on blur / Enter / select change.
  // Anchors (user-typed values) are never overwritten; derived values recalculate.
  const W_MM = { mm: 1, cm: 10, in: 25.4 };
  const T_UM = { um: 1, mil: 25.4 };
  const r2 = (n) => Math.round(n * 100) / 100;
  const recalc = (source, overrides = {}) => {
    const val = (o, s) => (overrides[o] !== undefined ? overrides[o] : s);
    const rct = val("rollContentType", rollContentType);
    const wUnit = val("rollWeightUnit", rollWeightUnit);
    const len = val("contentPerStockUnit", contentPerStockUnit);
    const net = val("rollNetWeight", rollNetWeight);
    const gross = val("rollGrossWeight", rollGrossWeight);
    const core = parseFloat(val("rollCoreWeight", rollCoreWeight)) || 0;
    const w = parseFloat(val("filmWidth", filmWidth));
    const th = parseFloat(val("filmThickness", filmThickness));
    const den = parseFloat(val("materialDensity", materialDensity));
    const gpm = ["ft", "m"].includes(rct) && w > 0 && th > 0 && den > 0
      ? w * (W_MM[val("filmWidthUnit", filmWidthUnit)] || 1) * th * (T_UM[val("filmThicknessUnit", filmThicknessUnit)] || 1) * den / 1000 : null;
    const toG = { kg: 1000, g: 1, oz: 28.3495, lbs: 453.592 }[wUnit] || 453.592;
    const netFromLen = (l) => {
      if (!(gpm > 0 && l > 0)) return null;
      const lenM = rct === "ft" ? l * 0.3048 : l;
      const n = r2((gpm * lenM) / toG);
      if (!(n > 0)) return null;
      netSource.current = "specs";
      setRollNetWeight(String(n));
      setRollGrossWeight(String(r2(n + core)));
      return n;
    };
    const lenFromNet = (n) => {
      if (!(gpm > 0 && n > 0)) return;
      const lenM = (n * toG) / gpm;
      lenSource.current = "derived";
      setContentPerStockUnit(String(Math.round(rct === "ft" ? lenM / 0.3048 : lenM)));
    };
    if (source === "length") {
      if (len === "") return;
      if (netSource.current !== "manual") netFromLen(parseFloat(len));
    } else if (source === "net") {
      if (net === "") {
        // cleared: re-derive from specs+length if possible
        if (len !== "" && netSource.current !== "manual") netFromLen(parseFloat(len));
        return;
      }
      setRollGrossWeight(String(r2(parseFloat(net) + core)));
      if (lenSource.current !== "manual") lenFromNet(parseFloat(net));
    } else if (source === "gross") {
      if (gross === "") return;
      if (["manual", "specs"].includes(netSource.current) && net !== "") {
        // net is anchored: back-fill core = gross − net
        setRollCoreWeight(String(Math.max(0, r2(parseFloat(gross) - parseFloat(net)))));
      } else {
        const n = Math.max(0, r2(parseFloat(gross) - core));
        netSource.current = "gross";
        setRollNetWeight(String(n));
        if (lenSource.current !== "manual") lenFromNet(n);
      }
    } else if (source === "core") {
      if (net !== "") setRollGrossWeight(String(r2(parseFloat(net) + core)));
      else if (gross !== "") {
        const n = Math.max(0, r2(parseFloat(gross) - core));
        netSource.current = "gross";
        setRollNetWeight(String(n));
        if (lenSource.current !== "manual") lenFromNet(n);
      }
    } else if (source === "specs") {
      if (len !== "" && netSource.current !== "manual") netFromLen(parseFloat(len));
      else if (net !== "" && netSource.current === "manual" && lenSource.current !== "manual") lenFromNet(parseFloat(net));
    }
  };
  const blurOnEnter = (e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } };
  const onNetChange = (raw) => {
    const v = raw.replace(/[^0-9.]/g, "");
    netSource.current = v === "" ? null : "manual";
    setRollNetWeight(v);
  };
  const onGrossChange = (raw) => {
    setRollGrossWeight(raw.replace(/[^0-9.]/g, ""));
  };
  const onCoreChange = (raw) => {
    setRollCoreWeight(raw.replace(/[^0-9.]/g, ""));
  };

  const lengthPerRollBlock = (
    <div className="space-y-1" key="len">
      <span className="text-[11px] text-slate-500">{isFilm || ["ft", "m"].includes(rollContentType) ? "Length per roll" : "Content per roll"}</span>
      <div className="flex gap-2">
        <Input type="text" inputMode="decimal" value={contentPerStockUnit} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); lenSource.current = v === "" ? null : "manual"; setContentPerStockUnit(v); }} onBlur={() => recalc("length")} onKeyDown={blurOnEnter} placeholder="e.g. 300" className={inputCls + " flex-1"} data-testid="type-content-per-roll" />
        <Select value={rollContentType} onValueChange={(v) => { setRollContentType(v); recalc("specs", { rollContentType: v }); }}>
          <SelectTrigger className="w-32 rounded-sm" data-testid="type-roll-content-type"><span>{rollContentType === "units" ? "Units" : rollContentType}</span></SelectTrigger>
          <SelectContent>
            {!isFilm && <SelectItem value="units">Units</SelectItem>}
            <SelectItem value="ft">ft</SelectItem>
            <SelectItem value="m">m</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-[11px] text-slate-400">{isFilm ? "How long each roll is — in metres or feet." : "How much each roll holds — pick units, length, or weight."}</p>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        image_url: imageUrl.trim(),
        description: description.trim(),
        attributes: attributes
          .map((a) => ({ label: a.label.trim(), value: a.value.trim() }))
          .filter((a) => a.label || a.value),
        designs: designs
          .map((d) => ({
            ...d,
            name: (d.name || "").trim(),
            associated_cap: "", associated_water_type: "", associated_additive: "",
            associations: (d.associations || []).filter((a) => a.variant_id).map((a) => ({
              id: a.id || null,
              component_id: a.component_id || "",
              variant_id: a.variant_id,
              dose_g_per_l: a.dose_g_per_l === "" || a.dose_g_per_l == null ? null : Number(a.dose_g_per_l),
            })),
          }))
          .filter((d) => d.name),
        tech_data_sheet_url: sheetUrl,
        tech_data_sheet_name: sheetName,
        units_per_case: unitsPerCase === "" ? null : Number(unitsPerCase),
        cases_per_pallet: casesPerPallet === "" ? null : Number(casesPerPallet),
        units_per_pallet: unitsPerPallet === "" ? null : Number(unitsPerPallet),
        display_unit: displayUnit.trim(),
        stock_unit_name: stockUnitName.trim(),
        content_per_stock_unit: contentPerStockUnit === "" ? null : Number(contentPerStockUnit),
        weight_per_unit: weightPerUnit === "" ? null : Number(weightPerUnit),
        weight_unit: weightUnit,
        packaging_mode: packagingMode,
        rolls_per_case: rollsPerCase === "" ? null : Number(rollsPerCase),
        roll_content_type: rollContentType,
        roll_gross_weight: rollGrossWeight === "" ? null : Number(rollGrossWeight),
        roll_net_weight: rollNetWeight === "" ? null : Number(rollNetWeight),
        roll_core_weight: rollCoreWeight === "" ? null : Number(rollCoreWeight),
        roll_weight_unit: rollWeightUnit,
        film_width: filmWidth === "" ? null : Number(filmWidth),
        film_width_unit: filmWidthUnit,
        film_thickness: filmThickness === "" ? null : Number(filmThickness),
        film_thickness_unit: filmThicknessUnit,
        material_density: materialDensity === "" ? null : Number(materialDensity),
        film_material: filmMaterial,
        package_type: packageType,
        packages_per_pallet: packagesPerPallet === "" ? null : Number(packagesPerPallet),
        weight_per_pallet: palletWeight === "" ? null : Number(palletWeight),
        pallet_weight_unit: palletWeightUnit,
        pallet_dims: palletDims.trim(),
        recipe: recipe.filter((r) => r.component_id).map((r) => ({
          id: r.id, component_id: r.component_id,
          variant_id: r.mode === "pick_at_bom" ? "" : (r.variant_id || ""),
          mode: r.mode || "fixed", quantity: Number(r.quantity) || 0,
          unit: r.unit || "", basis: r.basis || "per_bottle",
          per_volume_qty: r.per_volume_qty === "" || r.per_volume_qty == null ? null : Number(r.per_volume_qty),
          per_volume_amount: r.per_volume_amount === "" || r.per_volume_amount == null ? null : Number(r.per_volume_amount),
          per_volume_unit: r.per_volume_unit || "gal",
        })),
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const nameLabel = itemLabel?.trim() || "Name";
  const namePlaceholder = variationLabel?.trim()
    ? `e.g. a ${variationLabel.trim().toLowerCase()}`
    : `Name this ${(itemLabel || "item").toLowerCase()}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-4xl" data-testid="type-dialog">
        <DialogHeader>
          <DialogTitle className="text-left font-heading tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-left">
            Add a specific variation with its own image and specifications.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{nameLabel}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={namePlaceholder} className={inputCls} data-testid="type-name-input" autoFocus />
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
          <div className="space-y-6">
          {isLabel && (
            <div className="space-y-2 rounded-sm border border-slate-200 p-3" data-testid="designs-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Designs</Label>
                <span className="text-xs text-slate-400">Artwork variations for this label</span>
              </div>
              {designs.length === 0 && (
                <p className="text-xs text-slate-400">No designs yet. Add artwork variations that use this label spec.</p>
              )}
              {designs.map((d, i) => (
                <div key={d.id || i} className="rounded-sm border border-slate-200 p-2" data-testid={`design-row-${i}`}>
                  <div className="flex items-stretch gap-2">
                    <ImageUpload value={d.artwork_image_url || ""} onChange={(url) => patchDesign(i, { artwork_image_url: url })} testId={`design-photo-${i}`} compact />
                    <div className="flex flex-1 flex-col gap-1.5">
                      <div className="flex items-center gap-1">
                        <Input value={d.name} onChange={(e) => patchDesign(i, { name: e.target.value })} placeholder="Design name (e.g. Mickey Mouse)" className={inputCls + " h-8 flex-1 text-xs"} data-testid={`design-name-${i}`} />
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeDesign(i)} data-testid={`remove-design-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Associated components</span>
                      {(d.associations || []).map((a, ai) => {
                        const comp = (components || []).find((c) => c.id === a.component_id);
                        const isAdditive = /additive/i.test(comp?.name || "");
                        const vOpts = comp?.types || [];
                        const vName = vOpts.find((t) => t.id === a.variant_id)?.name;
                        const vMissing = a.variant_id && !vName;
                        return (
                          <div key={a._uid || a.id || ai} className="flex items-center gap-1" data-testid={`design-assoc-${i}-${ai}`}>
                            <Select value={a.component_id || undefined} onValueChange={(v) => patchAssoc(i, ai, { component_id: v, variant_id: "", dose_g_per_l: null })}>
                              <SelectTrigger className="h-8 w-28 shrink-0 rounded-sm text-xs" data-testid={`design-assoc-comp-${i}-${ai}`}>
                                <span className={comp ? "" : "text-slate-400"}>{comp?.name || "Component"}</span>
                              </SelectTrigger>
                              <SelectContent>
                                {(components || []).map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                            <Select value={a.variant_id || undefined} onValueChange={(v) => patchAssoc(i, ai, { variant_id: v })}>
                              <SelectTrigger className="h-8 flex-1 rounded-sm text-xs" data-testid={`design-assoc-variant-${i}-${ai}`}>
                                <span className={vName ? "" : (vMissing ? "text-amber-600" : "text-slate-400")}>{vName || (vMissing ? "Unavailable variant" : "Variant")}</span>
                              </SelectTrigger>
                              <SelectContent>
                                {vOpts.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                            {isAdditive && (
                              <Input value={a.dose_g_per_l ?? ""} onChange={(e) => patchAssoc(i, ai, { dose_g_per_l: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="g/L" title="Additive dose in grams per litre of water" className={inputCls + " h-8 w-14 shrink-0 text-xs"} data-testid={`design-assoc-dose-${i}-${ai}`} />
                            )}
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeAssoc(i, ai)} data-testid={`design-assoc-remove-${i}-${ai}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                      <button type="button" onClick={() => addAssoc(i)} className="flex items-center gap-1 text-[11px] font-medium text-[#002FA7] hover:underline" data-testid={`design-assoc-add-${i}`}>
                        <Plus className="h-3 w-3" /> Add component
                      </button>
                      <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">SKUs &amp; UPCs</span>
                      {["unit", "case", "pallet"].map((p) => (
                        <div key={p} className="flex items-center gap-1" data-testid={`design-sku-row-${i}-${p}`}>
                          <span className="w-11 shrink-0 text-[10px] font-semibold uppercase text-slate-500">{p}</span>
                          <Input value={d[`${p}_sku`] || ""} onChange={(e) => patchDesign(i, { [`${p}_sku`]: e.target.value })} placeholder="SKU" className={inputCls + " h-7 flex-1 text-xs"} data-testid={`design-${p}-sku-${i}`} />
                          <Input value={d[`${p}_upc`] || ""} onChange={(e) => patchDesign(i, { [`${p}_upc`]: e.target.value })} placeholder="UPC" className={inputCls + " h-7 flex-1 text-xs"} data-testid={`design-${p}-upc-${i}`} />
                          {d[`${p}_upc_barcode_url`] ? (
                            <span className="flex shrink-0 items-center gap-0.5">
                              <a href={resolveImageUrl(d[`${p}_upc_barcode_url`])} target="_blank" rel="noreferrer" title={d[`${p}_upc_barcode_name`] || "Barcode file"} className="text-[#002FA7] hover:text-[#002080]" data-testid={`design-${p}-barcode-link-${i}`}>
                                <FileText className="h-4 w-4" />
                              </a>
                              <button type="button" onClick={() => patchDesign(i, { [`${p}_upc_barcode_url`]: "", [`${p}_upc_barcode_name`]: "" })} className="text-slate-400 hover:text-red-600" title="Remove barcode file" data-testid={`design-${p}-barcode-remove-${i}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ) : (
                            <span className="shrink-0">
                              <input type="file" id={`bc-${i}-${p}`} accept=".pdf,.png,.jpg,.jpeg,.svg,.eps,.zip,.ai" className="hidden" onChange={(e) => handleBarcode(i, p, e)} data-testid={`design-${p}-barcode-input-${i}`} />
                              <label htmlFor={`bc-${i}-${p}`} title={`Attach ${p} UPC barcode file`} className="cursor-pointer text-slate-400 hover:text-[#002FA7]" data-testid={`design-${p}-barcode-upload-${i}`}>
                                <Upload className="h-4 w-4" />
                              </label>
                            </span>
                          )}
                        </div>
                      ))}
                      {isAlum && (
                        <div className="flex items-center gap-1" data-testid={`design-ball-row-${i}`}>
                          <span className="w-11 shrink-0 text-[10px] font-semibold uppercase text-slate-500">BALL</span>
                          <Input value={d.ball_code || ""} onChange={(e) => patchDesign(i, { ball_code: e.target.value })} placeholder="BALL Code (supplier order code)" className={inputCls + " h-7 flex-1 text-xs"} data-testid={`design-ball-code-${i}`} />
                        </div>
                      )}
                      <div className="flex items-center gap-1" data-testid={`design-sources-row-${i}`}>
                        <span className="w-11 shrink-0 text-[10px] font-semibold uppercase text-slate-500">Source</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="flex h-7 flex-1 items-center gap-1 overflow-hidden rounded-sm border border-slate-300 px-2 text-left text-xs hover:border-slate-400" data-testid={`design-sources-trigger-${i}`}>
                              {(d.sources || []).length > 0
                                ? sourceOptions.filter((s) => d.sources.includes(s.id)).map((s) => (
                                    <span key={s.id} className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{s.name}</span>
                                  ))
                                : <span className="text-slate-400">Where is this bottled?</span>}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-60 rounded-sm p-2">
                            <p className="mb-1 px-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">Bottled / printed at</p>
                            {sourceOptions.map((s) => (
                              <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs hover:bg-slate-50" data-testid={`design-source-opt-${i}-${s.id}`}>
                                <Checkbox checked={(d.sources || []).includes(s.id)} onCheckedChange={(v) => {
                                  const cur = d.sources || [];
                                  patchDesign(i, { sources: v ? [...cur, s.id] : cur.filter((x) => x !== s.id) });
                                }} />
                                {s.name}
                              </label>
                            ))}
                            {sourceOptions.length === 1 && <p className="px-1.5 py-1 text-[10px] text-slate-400">Add co-packers on the Contacts page to list more sources.</p>}
                          </PopoverContent>
                        </Popover>
                      </div>
                      <p className="text-[10px] text-slate-400">Picking this design on a BOM or order uses these variants. Additives take a g/L dose that scales with the product's water volume.</p>
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" className="w-full rounded-sm border-dashed" onClick={addDesign} data-testid="add-design-button">
                <Plus className="mr-1 h-4 w-4" /> Add Design
              </Button>
            </div>
          )}
          {!isLabel && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Photo</Label>
              <ImageUpload value={imageUrl} onChange={(v) => { setImageUrl(v); setImgError(false); }} testId="type-image-upload" />
            </div>
          )}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Technical Data Sheet</Label>
                <input ref={sheetRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" onChange={handleSheet} data-testid="type-datasheet-input" />
                {sheetUrl ? (
                  <div className="flex items-center justify-between rounded-sm border border-slate-200 px-3 py-2">
                    <a href={resolveImageUrl(sheetUrl)} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm text-[#002FA7] hover:underline" data-testid="type-datasheet-link">
                      <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{sheetName || "View data sheet"}</span>
                    </a>
                    <button type="button" onClick={() => { setSheetUrl(""); setSheetName(""); }} className="ml-2 shrink-0 text-slate-400 hover:text-red-600" data-testid="type-datasheet-remove"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="w-full justify-start rounded-sm" disabled={sheetUploading} onClick={() => sheetRef.current?.click()} data-testid="type-datasheet-upload">
                    {sheetUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload data sheet
                  </Button>
                )}
                <p className="text-xs text-slate-400">PDF, DOC, XLS, CSV or TXT · up to 25MB{isLabel ? " · one sheet per variant (applies to all designs)" : ""}</p>
              </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional details about this variation" className={inputCls} data-testid="type-description-input" />
          </div>
          </div>

          <div className="space-y-6">
          <div className="space-y-2 rounded-sm border border-slate-100 bg-slate-50 p-3" data-testid="type-packaging">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Packaging</Label>
              {forceRolls ? (
                <span className="rounded-sm bg-[#002FA7] px-3 py-1 text-[11px] font-semibold text-white" data-testid="packaging-mode-locked" title="This component is stocked as rolls">Rolls</span>
              ) : (
              <div className="inline-flex overflow-hidden rounded-sm border border-slate-300 text-[11px] font-semibold">
                <button type="button" onClick={() => setPackagingMode("standard")} className={`px-3 py-1 transition-colors ${packagingMode === "standard" ? "bg-[#002FA7] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`} data-testid="packaging-mode-standard">Units</button>
                <button type="button" onClick={() => { setPackagingMode("rolls"); if (!stockUnitName) setStockUnitName("roll"); }} className={`px-3 py-1 transition-colors ${packagingMode === "rolls" ? "bg-[#002FA7] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`} data-testid="packaging-mode-rolls">Rolls</button>
                <button type="button" onClick={() => { setPackagingMode("weight"); if (weightUnit === "g") setWeightUnit("lbs"); }} className={`px-3 py-1 transition-colors ${packagingMode === "weight" ? "bg-[#002FA7] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`} data-testid="packaging-mode-weight">Weight</button>
              </div>
              )}
            </div>
            {packagingMode === "weight" ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Package Type</span>
                    <Select value={packageType || undefined} onValueChange={setPackageType}>
                      <SelectTrigger className="w-full rounded-sm" data-testid="type-package-type">
                        <span className={packageType ? "capitalize" : "text-slate-400"}>{packageType || "Select"}</span>
                      </SelectTrigger>
                      <SelectContent>
                        {["bag", "pail", "drum", "tote", "other"].map((p) => (<SelectItem key={p} value={p} className="capitalize">{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Net Weight per Package</span>
                    <div className="flex gap-1">
                      <Input type="text" inputMode="decimal" value={weightPerUnit} onChange={(e) => setWeightPerUnit(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 50" className={inputCls + " min-w-0 flex-1"} data-testid="type-net-weight-per-package" />
                      <Select value={weightUnit} onValueChange={setWeightUnit}>
                        <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-package-weight-unit"><span>{weightUnit}</span></SelectTrigger>
                        <SelectContent>{["lbs", "oz", "kg", "g"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Packages per Pallet (optional)</span>
                    <Input type="text" inputMode="numeric" value={packagesPerPallet} onChange={(e) => setPackagesPerPallet(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 40" className={inputCls} data-testid="type-packages-per-pallet" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Net Weight per Pallet</span>
                    {parseFloat(weightPerUnit) > 0 && parseFloat(packagesPerPallet) > 0 ? (
                      <div className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800" data-testid="type-net-weight-per-pallet">
                        {(parseFloat(weightPerUnit) * parseFloat(packagesPerPallet)).toLocaleString()} {weightUnit}
                        <span className="ml-2 text-[11px] font-normal text-slate-400">= weight/package × packages/pallet</span>
                      </div>
                    ) : (
                      <div className="rounded-sm border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-400">Set package weight and packages/pallet</div>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400">Inventory for this variant is tracked by total weight in {weightUnit} (partial packages supported). Stock displays also show the package equivalent.</p>
              </>
            ) : packagingMode === "standard" ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Units per case</span>
                    <Input type="text" inputMode="numeric" value={unitsPerCase} onChange={(e) => setUnitsPerCase(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 24" className={inputCls} data-testid="type-units-per-case" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Cases per pallet</span>
                    <Input type="text" inputMode="numeric" value={casesPerPallet} onChange={(e) => setCasesPerPallet(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 50" className={inputCls} data-testid="type-cases-per-pallet" />
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">Units per pallet</span>
                  {parseFloat(unitsPerCase) > 0 && parseFloat(casesPerPallet) > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800" data-testid="type-units-per-pallet-auto">
                      {(parseFloat(unitsPerCase) * parseFloat(casesPerPallet)).toLocaleString()}
                      <span className="text-[11px] font-normal text-slate-400">= units/case × cases/pallet</span>
                    </div>
                  ) : (
                    <Input type="text" inputMode="numeric" value={unitsPerPallet} onChange={(e) => setUnitsPerPallet(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 40000 — enter directly" className={inputCls} data-testid="type-units-per-pallet" />
                  )}
                </div>
                <p className="text-[11px] text-slate-400">No cases? Leave the case fields blank and enter units per pallet directly.</p>
              </>
            ) : (
              <>
                {(() => {
                  const showSpecs = isFilm || ["ft", "m"].includes(rollContentType);
                  const specsBlock = showSpecs ? (() => {
                  const wMM = { mm: 1, cm: 10, in: 25.4 };
                  const tUM = { um: 1, mil: 25.4 };
                  const w = parseFloat(filmWidth), th = parseFloat(filmThickness), den = parseFloat(materialDensity);
                  const gpm = w > 0 && th > 0 && den > 0 ? w * (wMM[filmWidthUnit] || 1) * th * (tUM[filmThicknessUnit] || 1) * den / 1000 : null;
                  const lenM = parseFloat(contentPerStockUnit) > 0 ? parseFloat(contentPerStockUnit) * (rollContentType === "ft" ? 0.3048 : 1) : null;
                  const netG = gpm != null && lenM != null ? gpm * lenM : null;
                  const fmtW = (g) => `${(g / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} kg / ${(g / 453.592).toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs`;
                  const specsInner = (
                    <div className="space-y-2 rounded-sm border border-slate-200 bg-white p-2" data-testid="film-specs-section" key="specs">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Film specs</span>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <span className="text-[11px] text-slate-500">Width</span>
                          <div className="flex gap-1">
                            <Input type="text" inputMode="decimal" value={filmWidth} onChange={(e) => setFilmWidth(e.target.value.replace(/[^0-9.]/g, ""))} onBlur={() => recalc("specs")} onKeyDown={blurOnEnter} placeholder="e.g. 20" className={inputCls + " min-w-0 flex-1"} data-testid="type-film-width" />
                            <Select value={filmWidthUnit} onValueChange={(v) => { setFilmWidthUnit(v); recalc("specs", { filmWidthUnit: v }); }}>
                              <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-film-width-unit"><span>{filmWidthUnit}</span></SelectTrigger>
                              <SelectContent>{["in", "mm", "cm"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[11px] text-slate-500">Thickness</span>
                          <div className="flex gap-1">
                            <Input type="text" inputMode="decimal" value={filmThickness} onChange={(e) => setFilmThickness(e.target.value.replace(/[^0-9.]/g, ""))} onBlur={() => recalc("specs")} onKeyDown={blurOnEnter} placeholder="e.g. 60" className={inputCls + " min-w-0 flex-1"} data-testid="type-film-thickness" />
                            <Select value={filmThicknessUnit} onValueChange={(v) => { setFilmThicknessUnit(v); recalc("specs", { filmThicknessUnit: v }); }}>
                              <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-film-thickness-unit"><span>{filmThicknessUnit === "um" ? "µm" : "mil"}</span></SelectTrigger>
                              <SelectContent><SelectItem value="um">µm</SelectItem><SelectItem value="mil">mil</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[11px] text-slate-500">Material</span>
                          <Select value={filmMaterial || undefined} onValueChange={(v) => { setFilmMaterial(v); const m = FILM_MATERIALS.find((x) => x.key === v); if (m) { setMaterialDensity(String(m.density)); recalc("specs", { materialDensity: String(m.density) }); } }}>
                            <SelectTrigger className="w-full rounded-sm" data-testid="type-film-material">
                              <span className={filmMaterial ? "" : "text-slate-400"}>{FILM_MATERIALS.find((m) => m.key === filmMaterial)?.label || (materialDensity ? `${materialDensity} g/cm³` : "Select")}</span>
                            </SelectTrigger>
                            <SelectContent>
                              {FILM_MATERIALS.map((m) => (
                                <SelectItem key={m.key} value={m.key}>{m.label} — {m.density} g/cm³</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <span className="text-[11px] text-slate-500">Calculated film weight</span>
                        {gpm != null ? (
                          <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="type-film-calc-weight">
                            <span className="font-semibold text-slate-800">{gpm.toLocaleString(undefined, { maximumFractionDigits: 2 })} g/m</span>
                            {netG != null && (
                              <span className="text-slate-600"> · {fmtW(netG)} per roll{parseFloat(rollCoreWeight) > 0 ? ` (+ core = gross)` : ""}</span>
                            )}
                            <span className="block text-[11px] font-normal text-slate-400">= width × thickness × density {netG != null ? "× length per roll" : "— set length per roll, or enter roll net weight below to auto-fill length"}</span>
                          </div>
                        ) : (
                          <div className="rounded-sm border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-400">Set width, thickness and material to auto-calculate roll weight and BOM usage weight.</div>
                        )}
                      </div>
                    </div>
                  );
                  return specsInner;
                })() : null;
                  return isFilm ? specsBlock : <>{lengthPerRollBlock}{specsBlock}</>;
                })()}
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Roll Net Weight</span>
                    <div className="flex gap-1">
                      <Input type="text" inputMode="decimal" value={rollNetWeight} onChange={(e) => onNetChange(e.target.value)} onBlur={() => recalc("net")} onKeyDown={blurOnEnter} placeholder="e.g. 16.8" className={inputCls + " min-w-0 flex-1"} data-testid="type-roll-net-weight" />
                      <Select value={rollWeightUnit} onValueChange={(v) => { setRollWeightUnit(v); recalc("specs", { rollWeightUnit: v }); }}>
                        <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-roll-net-weight-unit"><span>{rollWeightUnit}</span></SelectTrigger>
                        <SelectContent>{["lbs", "kg", "g", "oz"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Roll Core Weight</span>
                    <div className="flex gap-1">
                      <Input type="text" inputMode="decimal" value={rollCoreWeight} onChange={(e) => onCoreChange(e.target.value)} onBlur={() => recalc("core")} onKeyDown={blurOnEnter} placeholder="e.g. 1.2" className={inputCls + " min-w-0 flex-1"} data-testid="type-roll-core-weight" />
                      <Select value={rollWeightUnit} onValueChange={(v) => { setRollWeightUnit(v); recalc("specs", { rollWeightUnit: v }); }}>
                        <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-roll-core-weight-unit"><span>{rollWeightUnit}</span></SelectTrigger>
                        <SelectContent>{["lbs", "kg", "g", "oz"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Roll Gross Weight</span>
                    <div className="flex gap-1">
                      <Input type="text" inputMode="decimal" value={rollGrossWeight} onChange={(e) => onGrossChange(e.target.value)} onBlur={() => recalc("gross")} onKeyDown={blurOnEnter} placeholder="e.g. 18" className={inputCls + " min-w-0 flex-1"} data-testid="type-roll-gross-weight" />
                      <Select value={rollWeightUnit} onValueChange={(v) => { setRollWeightUnit(v); recalc("specs", { rollWeightUnit: v }); }}>
                        <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-roll-gross-weight-unit"><span>{rollWeightUnit}</span></SelectTrigger>
                        <SelectContent>{["lbs", "kg", "g", "oz"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400">Net + core auto-fills gross; gross + core auto-fills net. Length per roll auto-fills from net weight when film specs are set.</p>
                {isFilm && lengthPerRollBlock}
                {!isFilm && (
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">Net material weight per {rollContentType === "units" ? "unit" : rollContentType}</span>
                  {parseFloat(rollNetWeight) > 0 && parseFloat(contentPerStockUnit) > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800" data-testid="type-roll-net-per-content">
                      {(parseFloat(rollNetWeight) / parseFloat(contentPerStockUnit)).toLocaleString(undefined, { maximumFractionDigits: 4 })} {rollWeightUnit}
                      <span className="text-[11px] font-normal text-slate-400">= net weight ÷ content per roll</span>
                    </div>
                  ) : (
                    <div className="rounded-sm border border-dashed border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-400">Set net weight and content per roll</div>
                  )}
                </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Rolls per case</span>
                    <Input type="text" inputMode="numeric" value={rollsPerCase} onChange={(e) => setRollsPerCase(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 4" className={inputCls} data-testid="type-rolls-per-case" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-slate-500">Cases per pallet</span>
                    <Input type="text" inputMode="numeric" value={casesPerPallet} onChange={(e) => setCasesPerPallet(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 40" className={inputCls} data-testid="type-cases-per-pallet-rolls" />
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">Rolls per pallet</span>
                  {parseFloat(rollsPerCase) > 0 && parseFloat(casesPerPallet) > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800" data-testid="type-rolls-per-pallet-auto">
                      {(parseFloat(rollsPerCase) * parseFloat(casesPerPallet)).toLocaleString()}
                      <span className="text-[11px] font-normal text-slate-400">= rolls/case × cases/pallet</span>
                    </div>
                  ) : (
                    <Input type="text" inputMode="numeric" value={unitsPerPallet} onChange={(e) => setUnitsPerPallet(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 160 — enter directly" className={inputCls} data-testid="type-rolls-per-pallet" />
                  )}
                </div>
                <p className="text-[11px] text-slate-400">No cases? Leave rolls/case and cases/pallet blank and enter rolls per pallet directly. A roll is one stock unit.</p>
              </>
            )}
            <div className="grid grid-cols-2 gap-2 border-t border-slate-200 pt-2">
              <div className="space-y-1">
                <span className="text-[11px] text-slate-500">Weight per pallet</span>
                <div className="flex gap-1">
                  <Input type="text" inputMode="decimal" value={palletWeight} onChange={(e) => setPalletWeight(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="e.g. 1200" className={inputCls + " min-w-0 flex-1"} data-testid="type-weight-per-pallet" />
                  <Select value={palletWeightUnit} onValueChange={setPalletWeightUnit}>
                    <SelectTrigger className="w-16 shrink-0 rounded-sm px-2" data-testid="type-pallet-weight-unit"><span>{palletWeightUnit}</span></SelectTrigger>
                    <SelectContent>{["lbs", "kg"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[11px] text-slate-500">Pallet dimensions (L × W × H)</span>
                <Input value={palletDims} onChange={(e) => setPalletDims(e.target.value)} placeholder="e.g. 40 × 48 × 50 in" className={inputCls} data-testid="type-pallet-dims" />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Used on co-packer shipments and bills of lading.</p>
          </div>

          {dim !== "weight" && !isFilm && packagingMode !== "weight" && (
            <div className="space-y-2 rounded-sm border border-slate-100 bg-slate-50 p-3" data-testid="type-weight">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Weight</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">{weightLabel}</span>
                  <Input type="text" inputMode="decimal" value={weightPerUnit} onChange={(e) => setWeightPerUnit(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={dim === "volume" ? "e.g. 1.0" : "e.g. 15.3"} className={inputCls} data-testid="type-weight-per-unit" />
                  {component?.default_weight_per_unit != null && weightPerUnit === "" && (
                    <p className="text-[11px] text-slate-400" data-testid="type-weight-inherit-hint">Inherits component default: {component.default_weight_per_unit} {component.default_weight_unit || "g"}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">Unit</span>
                  <Select value={weightUnit} onValueChange={setWeightUnit}>
                    <SelectTrigger className="rounded-sm" data-testid="type-weight-unit"><span>{weightUnit}</span></SelectTrigger>
                    <SelectContent>{["g", "kg", "lb", "oz", "g/cm³", "g/L"].map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Used for the shipping-weight rollup on Product BOMs.</p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Specifications</Label>
              <span className="text-xs text-slate-400">Label + value</span>
            </div>
            {attributes.map((a, i) => (
              <div key={a._uid} className="flex items-center gap-2" data-testid={`attr-row-${i}`}>
                <Input value={a.label} onChange={(e) => updateAttr(i, "label", e.target.value)} placeholder="e.g. Neck Finish" className={inputCls + " flex-1"} data-testid={`attr-label-${i}`} />
                <Input value={a.value} onChange={(e) => updateAttr(i, "value", e.target.value)} placeholder="e.g. 1881" className={inputCls + " flex-1"} data-testid={`attr-value-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeAttr(i)} data-testid={`remove-attr-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="w-full rounded-sm border-dashed" onClick={addAttr} data-testid="add-attribute-button">
              <Plus className="mr-1 h-4 w-4" /> Add specification
            </Button>
          </div>
          </div>
          </div>

          {component?.has_recipe && (
          <div className="space-y-2 rounded-sm border border-slate-200 p-3" data-testid="type-recipe">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Recipe · associated ingredients</Label>
              <span className="text-xs text-slate-400">Child components consumed with this variant</span>
            </div>
            {recipe.length === 0 && <p className="text-xs text-slate-400">No ingredients. Add child components (e.g. additives) with quantities.</p>}
            {recipe.map((r, i) => (
              <div key={r.id || i} className="grid grid-cols-12 items-center gap-2" data-testid={`recipe-row-${i}`}>
                <div className="col-span-3">
                  <Select value={r.component_id || undefined} onValueChange={(v) => patchRecipe(i, { component_id: v, variant_id: "" })}>
                    <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`recipe-comp-${i}`}><span className={r.component_id ? "" : "text-slate-400"}>{components.find((c) => c.id === r.component_id)?.name || "Component"}</span></SelectTrigger>
                    <SelectContent>{components.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  {r.mode === "pick_at_bom" ? (
                    <div className="px-2 text-xs italic text-slate-400">Pick at BOM</div>
                  ) : (
                    <Select value={r.variant_id || undefined} onValueChange={(v) => patchRecipe(i, { variant_id: v })}>
                      <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`recipe-variant-${i}`}><span className={r.variant_id ? "" : "text-slate-400"}>{variantsOf(r.component_id).find((t) => t.id === r.variant_id)?.name || "Variant"}</span></SelectTrigger>
                      <SelectContent>{variantsOf(r.component_id).map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="col-span-1">
                  {r.basis === "per_litre_parent" ? (
                    <div className="flex h-8 items-center justify-center rounded-sm border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400" data-testid={`recipe-qty-auto-${i}`} title="Calculated from the dosage below">auto</div>
                  ) : (
                    <Input value={r.quantity} onChange={(e) => patchRecipe(i, { quantity: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Qty" className={inputCls + " h-8 text-xs"} data-testid={`recipe-qty-${i}`} />
                  )}
                </div>
                <div className="col-span-1"><Input value={r.unit} onChange={(e) => patchRecipe(i, { unit: e.target.value })} placeholder="unit" className={inputCls + " h-8 text-xs"} data-testid={`recipe-unit-${i}`} /></div>
                <div className="col-span-2">
                  <Select value={r.basis} onValueChange={(v) => patchRecipe(i, { basis: v })}>
                    <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`recipe-basis-${i}`}><span>{BASIS_LABEL[r.basis] || r.basis}</span></SelectTrigger>
                    <SelectContent>{Object.entries(BASIS_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-1"><button type="button" onClick={() => patchRecipe(i, { mode: r.mode === "fixed" ? "pick_at_bom" : "fixed", variant_id: "" })} className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-[#002FA7]" data-testid={`recipe-mode-${i}`}>{r.mode === "fixed" ? "Fixed" : "Pick"}</button></div>
                <div className="col-span-1 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => removeRecipe(i)} data-testid={`recipe-remove-${i}`}><Trash2 className="h-4 w-4" /></Button></div>
                {r.basis === "per_litre_parent" && (
                  <div className="col-span-12 -mt-1 flex flex-wrap items-center gap-2 rounded-sm bg-slate-50 px-2 py-1.5" data-testid={`recipe-per-volume-${i}`}>
                    <span className="text-[11px] font-medium text-slate-500">Dosage:</span>
                    <Input value={r.per_volume_qty ?? ""} onChange={(e) => patchRecipe(i, { per_volume_qty: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="e.g. 18" className={inputCls + " h-7 w-20 text-xs"} data-testid={`recipe-pv-qty-${i}`} />
                    <span className="text-xs text-slate-500">{r.unit || "unit"} per</span>
                    <Input value={r.per_volume_amount ?? ""} onChange={(e) => patchRecipe(i, { per_volume_amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="e.g. 3600" className={inputCls + " h-7 w-24 text-xs"} data-testid={`recipe-pv-amount-${i}`} />
                    <Select value={r.per_volume_unit || "gal"} onValueChange={(v) => patchRecipe(i, { per_volume_unit: v })}>
                      <SelectTrigger className="h-7 w-16 rounded-sm px-2 text-xs" data-testid={`recipe-pv-unit-${i}`}><span>{r.per_volume_unit || "gal"}</span></SelectTrigger>
                      <SelectContent><SelectItem value="gal">gal</SelectItem><SelectItem value="L">L</SelectItem></SelectContent>
                    </Select>
                    <span className="text-[11px] text-slate-400">of parent water{(parseFloat(r.per_volume_qty) > 0 && parseFloat(r.per_volume_amount) > 0) ? ` — ≈ ${(parseFloat(r.per_volume_qty) / (parseFloat(r.per_volume_amount) * ((r.per_volume_unit || "gal") === "gal" ? 3.78541 : 1))).toPrecision(3)} ${r.unit || "unit"}/L` : ""}</span>
                  </div>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" className="w-full rounded-sm border-dashed" onClick={addRecipe} data-testid="add-recipe-button"><Plus className="mr-1 h-4 w-4" /> Add ingredient</Button>
          </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="type-cancel-button">Cancel</Button>
            <Button type="submit" disabled={loading || !name.trim()} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="type-save-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
